<template>
  <div class="pa-5">
    <shopping-cart-type-header
      :doctype="doctype"
      @launchCartAlertsModal="launchCartAlertsModal(doctype)"
    />
    <v-row class="mt-3">
      <v-col cols="12" md="4">
        <v-row>
          <v-col cols="12">
            <label class="w-100 font-weight-black mb-3 text-left"
              >Documento Relacionado</label
            >
            <v-textarea
              v-model="relatedDocs"
              rows="1"
              filled
              solo
              counter="10"
              maxlength="10"
              :error-messages="relatedDocError"
              @keydown="blockInvalidKeys"
            />
          </v-col>
          <v-col cols="12">
            <destination-selector
              v-model="destination"
              return-value
              :destinations="destinations"
              :loading="destinations.length === 0"
              @input="(e) => updateCurrentOrder({ destinationId: e })"
            />
          </v-col>
          <v-col cols="12">
            <product-search-card
              ref="searchForm"
              :disabled="isLoading"
              @submit="onProductSearchSubmit"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" md="5">
        <v-row>
          <v-col cols="12" md="6">
            <label class="w-100 font-weight-black mb-3 text-left"
              >CFDI Relacionado</label
            >
            <v-autocomplete
              v-model="relatedCFDI"
              :items="RELATED_CFDI_OPTIONS"
              hide-details
              dense
              solo
              @input="(e) => updateCurrentOrder({ relatedCFDI: e })"
            />
          </v-col>
          <v-col cols="12" md="6" align="start">
            <v-form v-if="origin !== 'recent'" @submit.prevent="saveOrderName">
              <label class="w-100 font-weight-black mb-3"
                >Orden de Compra</label
              >
              <v-text-field
                v-debounce="(e) => updateCurrentOrder({ name: e })"
                :value="order"
                :disabled="origin === 'recent'"
                dense
                solo
                @input="(v) => (orderName = v)"
                @keypress="alphaNumericSpecial($event)"
              />
            </v-form>
            <selected-order-chip
              v-else
              :order-name="selectedRecentOrder.name"
              @unselect="setRecentOrderSelected(null)"
            />
          </v-col>
          <v-col cols="12">
            <customer-credit-limit-bar
              :credit-limit="Number(customerInfo?.KLIMK)"
              :statement-balance="Number(customerInfo?.SKFOR)"
              :available-credit="Number(customerInfo?.CREDD)"
              :available-credit-current-order="availableCreditCurrentOrder"
            />

            <restricted-material-purchase-limit
              :doctype="doctype"
              :customer-code="Number(customerCode)"
            />
          </v-col>
          <v-col cols="12">
            <customer-history-shopping :customer-code="Number(customerCode)" />
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" md="3">
        <shopping-cart-action-buttons
          :doctype="doctype"
          :order-id="orderId"
          @openArchive="isArchiveUploadModalOpen = true"
          @scheduleOrder="isScheduleOrder"
          @scheduleOrderDate="isScheduleOrderDate"
          @onClickClearance="isClearanceMaterialsModalOpen = true"
          @orderCleaned="updateOrderTotal"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <shopping-cart-table
          :customer-code="Number(customerCode)"
          :doctype="doctype"
          @materialQuantityUpdated="updateOrderTotal"
          @materialDeleted="updateOrderTotal"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="7">
        <div class="d-flex flex-column flex-md-row flex-wrap action-btn-group">
          <v-btn
            v-if="doctype !== 'PSDT'"
            class="action-btn"
            @click="convertToNormalOrder"
          >
            <span class="icoiusa-checkbox-marked mr-2" />
            Convertir a Pedido Normal
          </v-btn>
          <v-btn
            v-if="doctype !== 'PSNW'"
            class="action-btn"
            @click="convertToNewProductsOrder"
          >
            <span class="icoiusa-two-arrows mr-2" />
            Convertir a Pedido de Productos Nuevos (PNC)
          </v-btn>
          <v-btn
            v-if="doctype !== 'PSPO'"
            class="action-btn"
            @click="convertToPromotionalProductsOrder"
          >
            <span class="icoiusa-shrink2 mr-2" />
            Convertir a Pedido Bono POP
          </v-btn>
        </div>
      </v-col>
      <v-col cols="12" md="5">
        <v-row>
          <v-col cols="12">
            <shopping-cart-price-summary
              :subtotal="subtotalCartPrice"
              :iva="ivaCartPrice"
              :total="totalCartPrice"
            />
          </v-col>
          <v-col cols="12">
            <v-btn
              v-if="!$hasAnyRole([Roles.MARKETING, Roles.POST_SALE])"
              class="confirm-order-btn"
              large
              block
              :loading="isOrderSubmitted"
              :disabled="isConfirmOrderButtonDisabled || isOrderSubmitted"
              @click="onConfirmOrder"
            >
              {{
                isConfirmOrderButtonDisabled
                  ? ' COMPRA EN PROCESO'
                  : 'CONFIRMAR PEDIDO'
              }}
              <v-icon class="ml-2">
                mdi-checkbox-multiple-marked-circle
              </v-icon>
            </v-btn>
          </v-col>
          <v-col
            cols="12"
            class="d-flex flex-column flex-md-row action-btn-group"
          >
            <v-btn
              class="action-btn"
              @click="$router.push({ name: homeRouteName })"
            >
              Carátula Cliente
            </v-btn>
            <v-btn class="action-btn" @click="exportToCSV">
              Generar Archivo CSV
            </v-btn>
            <v-btn class="action-btn" @click="convertOrderIntoQuote">
              Convertir a Cotización
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <confidentiality-notice />
    </v-row>

    <!-- MODALS, OVERLAYS AND TELEPORTS -->
    <generate-order-loading-modal v-model="isGeneratingOrder" />
    <confirm-backorder-modal
      v-model="isConfirmBackOrderModalOpen"
      :loading="isGeneratingOrder"
      :disabled="isConfirmOrderButtonDisabled"
      :submitted="isOrderSubmitted"
      @orderGenerateCredit="onConfirmBackOrder"
    />

    <confirm-cart-credit-modal
      v-model="isConfirmCartCreditModalOpen"
      :available-credit="availableCredit"
      :submitted="isOrderSubmitted"
      @orderGenerate="runGenerateOrder"
      @updateMaterialCredit="
        (isCredit, item) => onUpdateMaterialCredit(isCredit, item)
      "
    />

    <upload-order-by-archive
      v-model="isArchiveUploadModalOpen"
      :order="orderId"
      :customer="customerCode"
      :doctype="doctype"
      @materialTotalUpdated="updateOrderTotal"
    />

    <clearance-materials-modal
      v-if="isNewOrderDoctype"
      v-model="isClearanceMaterialsModalOpen"
      :customer="Number(customerCode)"
      :center="customerInfo.WERKS"
      :on-add-material-to-cart="onProductSearchSubmit"
    />

    <!-- Multiple carts alert -->
    <cart-conflict-alert
      v-if="!showCustomerChangedAlert"
      v-model="showSingleCartAlert"
      type="multiple-carts"
    />

    <!-- Customer changed alert -->
    <cart-conflict-alert
      v-model="showCustomerChangedAlert"
      type="customer-changed"
    />
  </div>
</template>

<script>
  import CustomerCreditLimitBar from './components/CustomerCreditLimitBar.vue';
  import DestinationSelector from './components/DestinationSelector.vue';
  import ShoppingCartTypeHeader from './components/ShoppingCartTypeHeader.vue';
  import ProductSearchCard from './components/ProductSearchCard.vue';
  import ShoppingCartActionButtons from './components/ShoppingCartActionButtons.vue';
  import ShoppingCartTable from './components/ShoppingCartTable.vue';
  import ConfidentialityNotice from '@/components/ConfidentialityNotice.vue';
  import ConfirmBackorderModal from './components/ConfirmBackorderModal.vue';
  import ConfirmCartCreditModal from './components/ConfirmCartCreditModal.vue';
  import GenerateOrderLoadingModal from './components/GenerateOrderLoadingModal.vue';
  import CustomerHistoryShopping from './components/CustomerHistoryShopping.vue';
  import ShoppingCartPriceSummary from '@/components/ShoppingCartPriceSummary.vue';
  import UploadOrderByArchive from './components/UploadOrderByArchive.vue';
  import SelectedOrderChip from './components/SelectedOrderChip.vue';
  import ClearanceMaterialsModal from './components/clearanceMaterials/ClearanceMaterialsModal.vue';

  import {
    MIN_ORDER_AMOUNT_DT,
    NPO_MIN_CUSTOMER_TENURE_MONTHS,
    NPO_MIN_INVOICED_LINES,
    NPO_MIN_PRODUCTS_IN_CART,
  } from '@/core/constants/orderConstants';
  import { Roles } from '@/core/constants/roleConstants';
  import { formatPrice as formatPriceUtil } from '@/utilities/formatPrice';
  import { alphaNumericSpecial as alphaNumericUtil } from '@/utilities/formatAlphanumeric';
  import gtagEvent from '@/utilities/gtagEvent';

  import userBlockedByBackorders from '@/mixins/userBlockedByBackorders';
  import cartOrderLifecycle from '@/mixins/cartOrderLifecycle';
  import IUSALoader from '@/mixins/fullScreenLoader';
  import axios from '@/plugins/axios';
  import maintenance from '@/mixins/maintenance';
  import relatedDocValidation from '@/mixins/relatedDocValidation';
  import cartTabConsistency from '@/mixins/cartTabConsistency';
  import RestrictedMaterialPurchaseLimit from './components/RestrictedMaterialPurchaseLimit.vue';

  import { mapGetters, mapActions, mapMutations } from 'vuex';
  import Papa from 'papaparse';
  import moment from 'moment';

  export default {
    name: 'NewShoppingCart',
    components: {
      CustomerCreditLimitBar,
      DestinationSelector,
      ShoppingCartTypeHeader,
      ProductSearchCard,
      ShoppingCartActionButtons,
      ShoppingCartTable,
      ConfidentialityNotice,
      ConfirmBackorderModal,
      ConfirmCartCreditModal,
      GenerateOrderLoadingModal,
      CustomerHistoryShopping,
      ShoppingCartPriceSummary,
      UploadOrderByArchive,
      SelectedOrderChip,
      ClearanceMaterialsModal,
      RestrictedMaterialPurchaseLimit,
    },
    mixins: [
      IUSALoader,
      userBlockedByBackorders,
      cartOrderLifecycle,
      maintenance,
      relatedDocValidation,
      cartTabConsistency,
    ],
    data: () => {
      return {
        isLoading: false,
        isOrderSubmitted: false,
        isGeneratingOrder: false,
        isLoadingCustomerInfo: true,
        isConfirmBackOrderModalOpen: false,
        isConfirmCartCreditModalOpen: false,
        RELATED_CFDI_OPTIONS: [
          { value: '00', text: '0 - SIN SELECCIÓN' },
          { value: '04', text: '04 - REFACTURACIÓN' },
        ],
        relatedCFDI: undefined,
        customerCode: undefined,
        customerTenure: undefined,
        customerInfo: undefined,
        destination: undefined,
        orderName: undefined,
        relatedDocs: undefined,
        scheduleOrder: undefined,
        scheduleOrderDate: undefined,
        isArchiveUploadModalOpen: false,
        isClearanceMaterialsModalOpen: false,
        relatedDocError: '',
        Roles,
      };
    },
    computed: {
      ...mapGetters('auth', ['user', 'userCode']),
      ...mapGetters('ordercart', [
        'cart',
        'destinations',
        'orderId',
        'order',
        'lowPrice',
        'itemsInCart',
        'subtotalCartPrice',
        'ivaCartPrice',
        'totalCartPrice',
        'allItemsBackorder',
      ]),
      ...mapGetters('shoppingCartAlerts', ['cartAlerts']),

      availableCredit() {
        return Number(this.customerInfo?.CREDD);
      },
      availableCreditCurrentOrder() {
        const availableCredit = Number(this.customerInfo?.CREDD);
        return availableCredit - this.totalCartPrice;
      },
      isConfirmOrderButtonDisabled() {
        const isCartEmpty = this.cart.length === 0;
        const isOrderSubmitted = this.isOrderSubmitted;
        const isBelowMinOrderAmount = this.totalCartPrice < MIN_ORDER_AMOUNT_DT;
        const hasBackOrders =
          this.userIsBlockedByBackorders && this.totalCartPrice != 0;
        const hasBackordersBelowMinAmount =
          this.allItemsBackorder && isBelowMinOrderAmount;

        if (this.scheduleOrder && !isCartEmpty && isBelowMinOrderAmount)
          return true;

        if (this.doctype === 'PSDT') {
          if (
            isCartEmpty ||
            isOrderSubmitted ||
            hasBackOrders ||
            (hasBackordersBelowMinAmount && this.totalCartPrice != 0)
          ) {
            return true;
          } else {
            if (this.cart.some((item) => item.recordatorios == 0)) {
              if (isBelowMinOrderAmount) {
                return true;
              } else {
                return false;
              }
            }
          }
        }

        if (this.doctype === 'PSPO') {
          if (isCartEmpty || isOrderSubmitted) return true;
          if (
            this.cart.some((item) => item.recordatorios === 0) &&
            this.totalCartPrice < 0
          )
            return true;
        }

        if (this.doctype === 'PSNW') {
          const isCartSmall = this.cart.length < NPO_MIN_PRODUCTS_IN_CART;
          const isCustomerTooRecent =
            this.customerTenure < NPO_MIN_CUSTOMER_TENURE_MONTHS;
          const hasExceededPPDP = this.customerInfo?.PPDP > 36;
          const hasInsufficientInvoicedLines =
            this.customerInfo?.RTRIMA < NPO_MIN_INVOICED_LINES;

          if (
            isCartEmpty ||
            isOrderSubmitted ||
            isBelowMinOrderAmount ||
            isCartSmall ||
            isCustomerTooRecent ||
            hasInsufficientInvoicedLines ||
            hasExceededPPDP
          )
            return true;
        }

        return false;
      },
      exceedsCreditLimit() {
        return this.totalCartPrice >= this.availableCredit ? true : false;
      },

      homeRouteName() {
        return this.userType === 'CL' ? 'home' : 'CustomerCoverDetails';
      },
    },
    watch: {
      order: {
        handler(order) {
          this.orderName = order;
        },
        immediate: true,
      },
      totalCartPrice() {
        this.runCartValidations();
        this.generateExcel();
      },
      relatedDocs(val) {
        this.validateRelatedDoc(val);

        if (!this.relatedDocError && val.length === 10) {
          this.updateCurrentOrder({ relatedDoc: val });
        }
      },
    },
    async mounted() {
      this.activeLoader();
      await this.prepareOrder();
      this.desactiveLoader();

      this.runCartValidations();

      if (this.cartAlerts.length || this.doctype === 'PSPO') {
        await this.launchCartAlertsModal(this.doctype);
      }
    },
    methods: {
      ...mapMutations('ordercart', ['SET_CUSTOMER_IVA']),
      ...mapActions('quotecart', ['turnOrderIntoQuoter']),
      ...mapActions('ordercart', [
        'getMaterialInfo',
        'loadCartItems',
        'loadCustomerDestinations',
        'loadOrder',
        'saveNewMaterialOrder',
        'updateMaterialQuantity',
        'updateMaterialCredit',
        'setOrderName',
        'generateOrder',
      ]),
      ...mapActions('modalStepsAfterLogin', [
        'updateModalState',
        'findStepByKey',
        'changeCurrentStepValue',
        'updateStepStatus',
        'loadRemindersStep',
        'loadShoppingCartAlertsStep',
      ]),
      ...mapActions('selectedUser', ['loadCustomer']),
      ...mapActions('shoppingCartAlerts', [
        'setShoppingCartAlertsDoctype',
        'clearShoppingCartAlerts',
        'setShoppingCartAlertChecked',
        'setShoppingCartAlertsInvalidCartItems',
      ]),
      ...mapActions('printer', ['sendToPDF', 'sendToExcel']),
      formatPrice(value) {
        return formatPriceUtil(value);
      },
      alphaNumericSpecial(value) {
        return alphaNumericUtil(value);
      },
      async launchBackOrdersModal() {
        await this.loadRemindersStep();
        const step = await this.findStepByKey('reminders');

        if (!step) return;

        step.visited = true;
        this.updateStepStatus(step);
        this.changeCurrentStepValue(step.value);
        this.updateModalState(true);
      },
      async launchCartAlertsModal(doctype, isConvert = false) {
        await this.loadShoppingCartAlertsStep({
          doctype,
          isBackOrder: false,
          isConvert,
        });
        const cartAlertsStep = await this.findStepByKey('shoppingCartAlerts');

        if (cartAlertsStep) {
          cartAlertsStep.visited = true;
          this.updateStepStatus(cartAlertsStep);
          this.changeCurrentStepValue(cartAlertsStep.value);
          this.updateModalState(true);
        }
      },
      generatePDF() {
        this.sendToPDF({ active: true });
      },
      generateExcel() {
        const itemsCSV = this.cart.map((item) => ({
          Producto: item.codigo_material,
          Nombre: item.nombre_material,
          Genetica: item.material_type,
          Cantidad: item.u_confirm,
          Empaque: item.empaque,
          'Unidad de Medida.': item.unidad_medida,
          'Pedido Mensual.': item.PMCOM,
          'Dias solicitados.': item.DSINV,
          'Precio de lista': item.precio_lista,
          'Precio C/ descuento': item.precio_con_descuento,
          'Importe Total': item.importe_producto,
        }));

        const currentDate = moment().format('YYYY/MM/DD');
        const objectCSV = {
          name: `Pedidos-${currentDate}`,
          items: itemsCSV,
          showLabels: true,
        };

        this.sendToExcel(objectCSV);
      },
      exportToCSV() {
        const csvData = [
          ['Versión', 'Código de Productos', 'Orden de Compra'],
          ['1.3.0', 'IUSA', this.orderName || 'OC-0'],
          ['Cantidad', 'Código', 'Nombre'],
        ];

        // Map cart data
        const rows = this.cart.map((item) => [
          parseInt(item.u_confirm),
          item.codigo_material,
          item.nombre_material,
        ]);

        csvData.push(...rows);
        const csvFile = Papa.unparse(csvData);

        const blob = new Blob([csvFile], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);

        const a = Object.assign(document.createElement('a'), {
          href: url,
          download: 'Pedidos.csv',
        });
        a.click();
      },
      runCartValidations() {
        this.clearShoppingCartAlerts();
        //VALIDATES BACKORDER ACTIVES
        if (this.userIsBlockedByBackorders) {
          this.setShoppingCartAlertChecked({
            id: 'BACKORDER_ACTIVE',
            isChecked: false,
          });
        }

        /// VALIDATES MIN PRICE IN CART FOR BOTH PSDT AND PSNW
        if (this.totalCartPrice < MIN_ORDER_AMOUNT_DT) {
          this.setShoppingCartAlertChecked({
            id: 'MIN_ORDER_AMOUNT_DT',
            isChecked: false,
          });
          this.setShoppingCartAlertChecked({
            id: 'MIN_ORDER_AMOUNT_NPO_DT',
            isChecked: false,
          });
        }

        // VALIDATES CUSTOMER AVAILABLE CREDIT
        if (this.totalCartPrice > this.availableCredit) {
          this.setShoppingCartAlertChecked({
            id: 'CUSTOMER_CREDIT_LIMIT',
            isChecked: false,
          });
        }

        // VALIDATES CUSTOMER DOCUMENTS EXPIRED
        if (this.customerInfo.DOCVENC > 0) {
          this.setShoppingCartAlertChecked({
            id: 'CUSTOMER_DOCVENC',
            isChecked: false,
          });
        }

        // VALIDATES MIN AMOUNT IN ITEMS IN CART (PER ITEM NOT QUANTITY)
        if (this.itemsInCart < NPO_MIN_PRODUCTS_IN_CART) {
          this.setShoppingCartAlertChecked({
            id: 'NPO_MIN_PRODUCTS_IN_CART',
            isChecked: false,
          });
        }

        // VALIDATES MIN CUSTOMER TENURE IN MONTHS
        if (this.customerTenure < NPO_MIN_CUSTOMER_TENURE_MONTHS) {
          this.setShoppingCartAlertChecked({
            id: 'NPO_MIN_CUSTOMER_TENURE_MONTHS',
            isChecked: false,
          });
        }

        // VALIDATES MIN CUSTOMER PPDP
        if (this.customerInfo.PPDP > 36) {
          this.setShoppingCartAlertChecked({
            id: 'MIN_CUSTOMER_PPDP',
            isChecked: false,
          });
        }

        // VALIDATES MIN CUSTOMER INVOICED LINES IN THE LAST TRIMESTER
        if (this.customerInfo.RTRIMA < NPO_MIN_INVOICED_LINES) {
          this.setShoppingCartAlertChecked({
            id: 'NPO_MIN_INVOICED_LINES',
            isChecked: false,
          });
        }

        // VALIDATES CUSTOMER BLOCKED BY AUSFD
        if (this.customerInfo.AUFSD == 1) {
          this.setShoppingCartAlertChecked({
            id: 'CUSTOMER_AUFSD',
            isChecked: false,
          });
        }
      },
      async convertOrderType(type) {
        try {
          this.activeLoader();

          await axios.post('changeOrderType', {
            order_id: this.orderId,
            order_type: type,
            channel: this.user.VTWEG,
          });

          const success = await this.syncConvertedOrder();
          this.desactiveLoader();

          if (success) {
            this.$router.replace({
              name: 'NewShoppingCart',
              params: { doctype: type, idq: this.idqParsed },
            });
          }
        } catch (error) {
          this.$toast.error(
            'Sucedió un error al convertir el pedido. Intentalo más tarde.'
          );
        }
      },
      async convertToNormalOrder() {
        await this.convertOrderType('PSDT');
      },
      async convertToNewProductsOrder() {
        if (this.itemsInCart === 0) {
          this.$toast.error(
            'El carrito está vacio, no se puede convertir a PNC'
          );
          return;
        }

        this.setShoppingCartAlertsDoctype('PSNW');
        this.runCartValidations();

        const nonNewProducts = this.cart.filter(
          (item) =>
            item.TIPOP === '1' ||
            item.PNC === 0 ||
            item.sector === 30 ||
            item.sector === 90
        );

        // VALIDATE IF PRODUCTS ARE INVALID TO PSPO ORDER
        if (nonNewProducts.length) {
          this.setShoppingCartAlertsInvalidCartItems(nonNewProducts);
          this.setShoppingCartAlertChecked({
            id: 'VALID_CART_ITEMS_PSNW',
            isChecked: false,
          });
        }

        if (this.cartAlerts.length) {
          await this.launchCartAlertsModal('PSNW', true);
        } else {
          await this.convertOrderType('PSNW');
        }
      },
      async convertToPromotionalProductsOrder() {
        if (this.itemsInCart === 0) {
          this.$toast.error(
            'El carrito está vacio, no se puede convertir a POP'
          );
          return;
        }

        this.setShoppingCartAlertsDoctype('PSPO');
        this.runCartValidations();

        const nonPromotionalProducts = this.cart.filter(
          (item) =>
            item.MVGR4 !== '1ZA' &&
            item.MVGR4 !== '1ZB' &&
            item.MVGR4 !== '1ZC' &&
            item.MVGR4 !== '1YA'
        );

        // VALIDATE IF PRODUCTS ARE INVALID TO PSPO ORDER
        if (nonPromotionalProducts.length) {
          this.setShoppingCartAlertsInvalidCartItems(nonPromotionalProducts);
          this.setShoppingCartAlertChecked({
            id: 'VALID_CART_ITEMS_PSPO',
            isChecked: false,
          });
        }

        if (this.cartAlerts.length) {
          await this.launchCartAlertsModal('PSPO', true);
        } else {
          await this.convertOrderType('PSPO');
        }
      },
      async convertOrderIntoQuote() {
        try {
          await this.turnOrderIntoQuoter({
            custumer_code: this.customerCode,
            order_id: this.orderId,
            user_id: this.user.id,
            cart: this.cart,
            type: this.userType,
            doctype: this.doctype,
            channel: this.user.VTWEG,
            total: this.totalCartPrice,
          });

          this.$router.push({ name: 'QuoterHistory' });
        } catch (error) {
          this.$toast.error('No se pudo convertir a cotización');
        }
      },
      async getCustomerInfo() {
        this.isLoadingCustomerInfo = true;
        const endpointUrl = 'infoCustomer';

        try {
          const response = await axios.post(endpointUrl, {
            customer: this.customerCode,
            VKORG: this.user.VKORG,
            VTWEG: this.user.VTWEG,
            VKBUR: this.user.VKBUR,
          });

          this.customerInfo = response.data;
          const blocked = await this.redirectIfBillingModuleDisabled(
            response.data?.OPCFAC
          );
          if (blocked) return;
          this.customerTenure = moment().diff(this.customerInfo.ERDAT, 'month');
          this.SET_CUSTOMER_IVA(this.customerInfo.TAXKD);

          return { ok: true, customerInfo: response.data };
        } catch (error) {
          this.$toast.error('No se pudo obtener la información del cliente');
          return { ok: false, customerInfo: undefined };
        } finally {
          this.isLoadingCustomerInfo = false;
        }
      },
      async saveOrderName() {
        if (this.order === this.orderName) {
          return;
        }

        await this.updateCurrentOrder({ name: this.orderName });
        this.setOrderName(this.orderName);

        if (!this.recentOrdersMutuationError)
          this.$toast.success('Se actualizó el nombre de la orden');
        else this.$toast.error(this.recentOrdersMutuationError);
      },
      async onProductSearchSubmit({ sku, quantity }) {
        // SEARCH SKU IN CURRENT CART
        const index = this.cart.findIndex(
          (item) => item.codigo_material == sku
        );
        if (index !== -1) {
          this.$toast.warning('El código de material ya está en el carrito');
          return;
        }

        await this.addItemToCart(sku, quantity);
      },
      async addItemToCart(sku, quantity) {
        this.isLoading = true;

        try {
          const product = await this.getMaterialInfo({
            sku,
            quantity,
            custumer_code: this.customerCode,
            VKORG: this.user.VKORG,
            VTWEG: this.user.VTWEG,
            VKBUR: this.user.VKBUR,
            order_type: this.doctype,
          });

          await this.saveNewMaterialOrder({
            order_id: this.orderId,
            item: product,
          });

          if (quantity < product.empaque || product.u_confirm == 0) {
            await this.updateMaterialQuantity({
              codigo_material: product.codigo_material,
              quantity:
                product.u_confirm == 0
                  ? product.recordatorios
                  : product.u_confirm,
              precio_con_descuento: '',
              custumer_code: this.customerCode,
              order_type: this.doctype,
            });
          }

          this.$refs.searchForm.clearForm();
          this.$toast.success('Producto agregado');
          this.updateOrderTotal();
        } catch (error) {
          if (error.response?.data?.error_code) {
            this.$toast.warning(error.response.data.error_code);
          } else {
            this.$toast.error('No se pudo agregar al carrito');
          }
        } finally {
          this.isLoading = false;
        }
      },
      async onConfirmOrder() {
        if (!this.destination) {
          this.$toast.error('El destinatario de mercancía es requerido');
          return;
        }

        if (!this.cart || this.cart.length === 0) {
          this.$toast.error(
            'El carrito está vacío, no se puede generar la orden'
          );
          return;
        }

        const existBackOrder = this.cart.some((item) => item.recordatorios > 0);

        if (existBackOrder) {
          this.isConfirmBackOrderModalOpen = true;
        } else {
          if (this.exceedsCreditLimit) {
            if (
              this.customerInfo.DOCVENC > 0 ||
              this.customerInfo.AUFSD == 1 ||
              this.availableCredit <= 1 ||
              (this.lowPrice > this.availableCredit && this.exceedsCreditLimit)
            ) {
              this.runGenerateOrder();
            } else {
              this.isConfirmCartCreditModalOpen = true;
            }
          } else {
            this.runGenerateOrder();
          }
        }
      },
      async onConfirmBackOrder() {
        if (this.allItemsBackorder) {
          this.runGenerateOrder();
        } else {
          if (this.exceedsCreditLimit) {
            if (
              this.customerInfo.DOCVENC > 0 ||
              this.customerInfo.AUFSD == 1 ||
              this.availableCredit <= 1 ||
              (this.lowPrice > this.availableCredit && this.exceedsCreditLimit)
            ) {
              this.runGenerateOrder();
            } else {
              this.isConfirmCartCreditModalOpen = true;
            }
          } else {
            this.runGenerateOrder();
          }
        }
      },
      async onUpdateMaterialCredit(isCredit, item) {
        try {
          await this.updateMaterialCredit({
            orderId: item.orderID,
            sku: item.materialCode,
            credit: isCredit,
            custumer: this.customerCode,
            quantity: item.orderUnits,
            doctype: this.doctype,
            VKORG: this.user.VKORG,
            VTWEG: this.user.VTWEG,
            VKBUR: this.user.VKBUR,
          });
        } catch (error) {
          this.$toast.error('No se pudo actualizar el crédito del material');
        }
      },
      async runGenerateOrder() {
        this.isConfirmBackOrderModalOpen = false;
        this.isConfirmCartCreditModalOpen = false;
        this.isGeneratingOrder = true;
        this.isOrderSubmitted = true;
        let purchaseValidation;

        if (
          this.customerInfo.DOCVENC > 0 ||
          this.customerInfo.AUFSD == 1 ||
          this.availableCredit <= 1 ||
          (this.lowPrice > this.availableCredit && this.exceedsCreditLimit)
        ) {
          purchaseValidation = 2;
        } else {
          purchaseValidation = 1;
        }

        try {
          gtagEvent.startEvent();
          const response = await this.generateOrder({
            user_id: this.user.id,
            custumer_code: this.customerCode,
            orden_compra: this.orderName,
            carrito: this.cart,
            orderType: this.doctype,
            destiny: parseInt(this.destination),
            banderaProgramado: '',
            purchase_validation: purchaseValidation,
            numero_vendedor: this.userCode,
            schedule_orders: this.scheduleOrder,
            schedule_date: this.scheduleOrderDate,
            quoter: this.idq,
          });

          this.isGeneratingOrder = false;
          this.isOrderSubmitted = false;

          if (response.message == 'BACKORDER GENERADO') {
            this.$swal.fire(
              response.message,
              'Número de Pedido: S/N',
              'success'
            );
            this.$router.replace({
              name: 'HistoryOrders',
            });
          } else {
            const orderResponse = response.VBELN;
            const orderSecondResponse =
              response.VBELN2 == ''
                ? ''
                : `Pedido enviado a crédito ${response.VBELN2}`;
            const invoiceResponse = response.FACTURAS;
            const deliveryResponse = response.ENTREGAS;
            const purchaseResponse =
              response.PVALID == 1 ? '' : '* Bloqueado por Crédito';
            const messageResponse = response.MYERR == 0 ? '' : response.FNMSG;

            const message = `Pedido: ${orderResponse}.
                 Entrega: ${deliveryResponse}.
                 Factura: ${invoiceResponse}.
                 ${orderSecondResponse}.
                 ${purchaseResponse}.
                 ${messageResponse}.`;

            this.$swal({
              title: 'Pedido Generado',
              text: message,
              icon: 'success',
              type: 'succes',
              confirmButtonText: 'CONTINUAR',
              confirmButtonColor: '#A82222',
              closeOnConfirm: true,
              allowOutsideClick: false,
              allowEscapeKey: false,
            });

            gtagEvent.orderCompleted(
              this.$gtag,
              orderResponse,
              this.user.VTWEG,
              this.doctype,
              this.itemsInCart
            );

            this.$router.replace({ name: 'HistoryOrders' });
          }
        } catch (error) {
          this.isGeneratingOrder = false;
          this.isOrderSubmitted = false;

          if (error?.response?.data?.message !== 'BACKORDER GENERADO') {
            gtagEvent.orderFailed(
              this.$gtag,
              this.user.VTWEG,
              this.doctype,
              this.itemsInCart
            );
          }

          if (error.response.status == 404) {
            if (error.response.data.message == 'BACKORDER GENERADO') {
              this.$swal.fire(
                error.response.data.message,
                'Número de Pedido: S/N',
                'success'
              );
              this.$router.replace({ name: 'HistoryOrders' });
            } else {
              this.$swal({
                title: error.response.data.error_code,
                icon: 'error',
                type: 'error',
              });
            }
          }
        }
      },
    },
  };
</script>

<style scoped>
  .confirm-order-btn {
    background: transparent linear-gradient(180deg, #218838 0%, #11441c 100%) 0%
      0% no-repeat padding-box;
    box-shadow: inset 0px 3px 6px #00000029, 0px 3px 6px #00000029;
    color: white !important;
    font-weight: 900 !important;
  }

  .action-btn {
    box-shadow: inset 0px 2px 6px #00000029, 0px 2px 6px #00000029;
    font-weight: 900;
    text-transform: capitalize;
    flex-grow: 1;
  }

  .action-btn-group {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 8px;
  }

  @media (min-width: 960px) {
    .action-btn-group {
      flex-direction: row;
      gap: 16px;
    }
  }
</style>
